import { useEffect, useState } from 'react';

export default function Header() {
  const [scrollY, setScrollY] = useState(0);

  // ✅ Lazy init: dark por defecto (respeta localStorage si existe)
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark') return true;
      if (saved === 'light') return false;

      localStorage.setItem('theme', 'dark');
      return true;
    } catch (e) {
      return true;
    }
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', darkMode);
    try {
      localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    } catch (e) {}
  }, [darkMode]);

  useEffect(() => {
    const onHashChange = () => setMobileOpen(false);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // ✅ helper: asegura scroll al section correcto (Services.jsx / Process.jsx)
  const goToSection = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // actualiza la URL hash sin recargar
      window.history.replaceState(null, '', `#${id}`);
    } else {
      // fallback si no existe aún
      window.location.hash = `#${id}`;
    }
    setMobileOpen(false);
  };

  const navLinkClass =
    'relative text-[11px] font-black text-slate-500 dark:text-slate-300 uppercase tracking-[0.18em] transition-colors duration-300 hover:text-slate-900 dark:hover:text-white';

  const underlineClass =
    "after:content-[''] after:absolute after:left-0 after:-bottom-2 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-lime-400 after:shadow-[0_0_14px_rgba(163,230,53,0.65)] after:transition-transform after:duration-300 after:ease-out hover:after:scale-x-100";

  // ✅ Icono WhatsApp "clásico" (logo dentro de burbuja)
  const WhatsAppIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
      <path d="M19.11 17.71c-.28-.14-1.64-.81-1.89-.9-.26-.1-.44-.14-.62.14-.18.28-.71.9-.87 1.08-.16.18-.33.2-.61.07-.28-.14-1.18-.43-2.24-1.38-.83-.73-1.39-1.64-1.55-1.92-.16-.28-.02-.43.12-.57.12-.12.28-.33.42-.49.14-.16.18-.28.28-.47.09-.18.05-.35-.02-.49-.07-.14-.62-1.51-.85-2.07-.22-.54-.45-.47-.62-.48-.16-.01-.35-.01-.53-.01-.19 0-.48.07-.73.35-.25.28-.96.94-.96 2.32 0 1.37.98 2.7 1.12 2.89.14.19 1.96 3 4.75 4.2.66.29 1.18.46 1.59.59.67.22 1.27.19 1.75.12.53-.08 1.64-.67 1.87-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.19-.53-.33z" />
      <path d="M16.01 3C8.83 3 3 8.83 3 16c0 2.55.74 5.01 2.14 7.13L3.74 28.3l5.32-1.39A12.9 12.9 0 0 0 16 29c7.17 0 13-5.83 13-13S23.18 3 16.01 3zm0 23.7c-2.1 0-4.15-.56-5.93-1.63l-.43-.25-3.15.82.84-3.06-.28-.44A10.66 10.66 0 0 1 5.34 16c0-5.9 4.8-10.7 10.67-10.7 5.88 0 10.66 4.8 10.66 10.7 0 5.9-4.78 10.7-10.66 10.7z" />
    </svg>
  );

  return (
    <header className="fixed top-6 w-full z-50 flex justify-center px-4">
      <nav
        className={[
          'rounded-[2rem] px-4 py-3 max-w-5xl w-full relative',
          'transition-all duration-500',
          'border border-white/35 dark:border-white/10',
          'bg-white/40 dark:bg-slate-950/30 backdrop-blur-xl',
          'shadow-[0_18px_60px_rgba(2,6,23,0.12)] dark:shadow-[0_18px_70px_rgba(0,0,0,0.45)]',
          scrollY > 50 ? 'py-2' : 'py-3',
        ].join(' ')}
      >
        <div className="pointer-events-none absolute inset-x-6 -top-[1px] h-[1px] bg-gradient-to-r from-transparent via-lime-400/70 to-transparent opacity-60" />

        <a href="#top" className="flex items-center justify-center" aria-label="Ir al inicio" title="Inicio">
          <img
            src="/images/logo-principal-web.svg"
            alt="Logo Principal"
            className={[
              'h-10 md:h-12 w-auto transition-transform duration-500 hover:scale-[1.02]',
              'dark:brightness-[1.35] dark:contrast-[1.25] dark:saturate-[1.25]',
              'dark:drop-shadow-[0_8px_18px_rgba(54,98,226,0.35)]',
            ].join(' ')}
            loading="eager"
          />
        </a>

        <div className="mt-2 w-full flex items-center justify-between gap-3">
          <div className="hidden md:flex items-center gap-10 pl-2">
            {/* ✅ Services.jsx -> section id="services" */}
            <a className={`${navLinkClass} ${underlineClass}`} href="#services" onClick={goToSection('services')}>
              Servicios
            </a>

            {/* ✅ Process.jsx -> ponle id="process" en tu section de Process.jsx */}
            <a className={`${navLinkClass} ${underlineClass}`} href="#process" onClick={goToSection('process')}>
              Proceso
            </a>

            <a className={`${navLinkClass} ${underlineClass}`} href="#about" onClick={goToSection('about')}>
              Agencia
            </a>
          </div>

          <div className="flex-1 flex items-center justify-end gap-3">
            {/* Desktop socials */}
            <div className="hidden sm:flex items-center gap-2">
              <a
                className="w-8 h-8 rounded-full glass-pill-nav flex items-center justify-center hover:bg-blue-600 hover:text-white text-slate-600 dark:text-slate-200 transition-all text-[16px]"
                href="https://www.facebook.com/share/1J2FRes2aK/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                title="Facebook"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>

              <a
                className="w-8 h-8 rounded-full glass-pill-nav flex items-center justify-center hover:bg-green-500 hover:text-white text-slate-600 dark:text-slate-200 transition-all text-[16px]"
                href="https://wa.me/19144634702"
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
              >
                <WhatsAppIcon size={16} />
              </a>

              <a
                className="w-8 h-8 rounded-full glass-pill-nav flex items-center justify-center hover:bg-red-500 hover:text-white text-slate-600 dark:text-slate-200 transition-all text-[16px]"
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                title="YouTube"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>

              <a
                className="w-8 h-8 rounded-full glass-pill-nav flex items-center justify-center hover:bg-pink-500 hover:text-white text-slate-600 dark:text-slate-200 transition-all text-[16px]"
                href="https://www.instagram.com/nextglobalmedia.us?igsh=eDcxNWJybmFvczZn&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                title="Instagram"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.8 2h8.4A5.8 5.8 0 0 1 22 7.8v8.4A5.8 5.8 0 0 1 16.2 22H7.8A5.8 5.8 0 0 1 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2Zm-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6A3.6 3.6 0 0 0 16.4 4H7.6Zm10.65 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
                </svg>
              </a>

              <a
                className="w-8 h-8 rounded-full glass-pill-nav flex items-center justify-center hover:bg-black hover:text-white text-slate-600 dark:text-slate-200 transition-all text-[16px]"
                href="https://tiktok.com"
                target="_blank"
                rel="noopener noreferrer"
                title="TikTok"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 5.2-1.74V9.4a6.84 6.84 0 0 0-1-.118 6.852 6.852 0 0 0-6.852 6.852A6.852 6.852 0 0 0 12.06 22.8a6.844 6.844 0 0 0 6.852-6.852v-3.29a4.828 4.828 0 0 1 3.077 1.18v-3.45a4.83 4.83 0 0 1-2.4-.677z" />
                </svg>
              </a>
            </div>

            {/* Toggle dark mode */}
            <button
              type="button"
              role="switch"
              aria-checked={darkMode}
              aria-label={darkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              onClick={() => setDarkMode((v) => !v)}
              className="
                relative inline-flex items-center
                h-8 w-14 rounded-full
                bg-slate-100 dark:bg-slate-900
                border-2 border-lime-400 dark:border-lime-300
                shadow-[0_0_0_1px_rgba(182,255,0,0.35)_inset,0_0_18px_rgba(182,255,0,0.35)]
                transition-colors
                focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2
                dark:focus-visible:ring-offset-slate-950
              "
              title={darkMode ? 'Modo oscuro: ON' : 'Modo oscuro: OFF'}
            >
              <span className="absolute left-2 text-[12px] text-slate-500 dark:text-slate-400 select-none">☀</span>
              <span className="absolute right-2 text-[12px] text-slate-500 dark:text-slate-400 select-none">🌙</span>
              <span
                className={[
                  'absolute left-1 top-1 h-6 w-6 rounded-full',
                  'bg-white dark:bg-slate-200 shadow',
                  'transition-transform duration-200',
                  darkMode ? 'translate-x-6' : 'translate-x-0',
                ].join(' ')}
              />
            </button>

            {/* Desktop CTA stays */}
            <a
              className="hidden md:flex bg-lime-400 hover:bg-lime-500 text-slate-950 pl-4 pr-1 py-1.5 rounded-full font-black text-[10px] tracking-widest transition-all hover:shadow-[0_4px_18px_rgba(163,230,53,0.45)] items-center gap-3 group"
              href="#contact"
              onClick={goToSection('contact')}
            >
              SOLICITA TU COTIZACIÓN
              <div className="bg-black/10 rounded-full p-1.5 group-hover:bg-white group-hover:text-slate-950 transition-colors">
                <span className="material-symbols-outlined text-[16px] leading-none transform -rotate-45 group-hover:rotate-0 transition-transform duration-300">
                  arrow_forward
                </span>
              </div>
            </a>

            {/* Mobile CTA: solo UNO */}
            <a
              className="md:hidden bg-lime-400 hover:bg-lime-500 text-slate-950 px-4 py-2 rounded-full font-black text-[10px] tracking-widest transition-all hover:shadow-[0_4px_18px_rgba(163,230,53,0.45)] flex items-center gap-2"
              href="#contact"
              onClick={goToSection('contact')}
            >
              COTIZA
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </a>

            {/* Mobile menu button: 9-dot grid */}
            <button
              type="button"
              className="md:hidden ml-1 w-10 h-10 rounded-xl glass-pill-nav flex items-center justify-center text-slate-600 dark:text-slate-200"
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? (
                <span className="material-symbols-outlined">close</span>
              ) : (
                <span className="grid grid-cols-3 gap-[3px]">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <span key={i} className="w-[4px] h-[4px] rounded-[2px] bg-slate-600 dark:bg-slate-200 opacity-90" />
                  ))}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile dropdown: secciones + sociales */}
        <div
          className={[
            'md:hidden w-full overflow-hidden transition-[max-height,opacity] duration-300',
            mobileOpen ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0',
          ].join(' ')}
        >
          <div className="mt-3 rounded-2xl border border-white/40 dark:border-white/10 bg-white/60 dark:bg-slate-950/40 backdrop-blur-md p-4">
            <div className="flex flex-col gap-3">
              <a className={`${navLinkClass} ${underlineClass} w-fit`} href="#services" onClick={goToSection('services')}>
                Servicios
              </a>
              <a className={`${navLinkClass} ${underlineClass} w-fit`} href="#process" onClick={goToSection('process')}>
                Proceso
              </a>
              <a className={`${navLinkClass} ${underlineClass} w-fit`} href="#about" onClick={goToSection('about')}>
                Agencia
              </a>

              <div className="pt-3 mt-2 border-t border-white/40 dark:border-white/10" />

              <div className="flex items-center justify-center gap-3">
                <a
                  className="w-11 h-11 rounded-2xl glass-pill-nav flex items-center justify-center hover:bg-green-500 hover:text-white text-slate-700 dark:text-slate-200 transition-all"
                  href="https://wa.me/19144634702"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="WhatsApp"
                  onClick={() => setMobileOpen(false)}
                >
                  <WhatsAppIcon size={18} />
                </a>

                <a
                  className="w-11 h-11 rounded-2xl glass-pill-nav flex items-center justify-center hover:bg-pink-500 hover:text-white text-slate-700 dark:text-slate-200 transition-all"
                  href="https://www.instagram.com/nextglobalmedia.us?igsh=eDcxNWJybmFvczZn&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.8 2h8.4A5.8 5.8 0 0 1 22 7.8v8.4A5.8 5.8 0 0 1 16.2 22H7.8A5.8 5.8 0 0 1 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2Zm-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6A3.6 3.6 0 0 0 16.4 4H7.6Zm10.65 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
                  </svg>
                </a>

                <a
                  className="w-11 h-11 rounded-2xl glass-pill-nav flex items-center justify-center hover:bg-red-500 hover:text-white text-slate-700 dark:text-slate-200 transition-all"
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="YouTube"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>

                <a
                  className="w-11 h-11 rounded-2xl glass-pill-nav flex items-center justify-center hover:bg-black hover:text-white text-slate-700 dark:text-slate-200 transition-all"
                  href="https://tiktok.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="TikTok"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 5.2-1.74V9.4a6.84 6.84 0 0 0-1-.118 6.852 6.852 0 0 0-6.852 6.852A6.852 6.852 0 0 0 12.06 22.8a6.844 6.844 0 0 0 6.852-6.852v-3.29a4.828 4.828 0 0 1 3.077 1.18v-3.45a4.83 4.83 0 0 1-2.4-.677z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}